// Update prompt (app-store-infra spec 09). An installed PWA keeps running
// whatever code it loaded — iOS suspends/resumes it for days without a
// reload — so after a deploy, stale clients hit changed APIs. This module
// notices a new deploy on resume and offers a one-tap reload.
//
// Design: the client never needs its own version baked in. It remembers the
// /api/version id it saw at load; a *different* id later means "the server
// changed since I loaded". Checks are resume-triggered (visibilitychange —
// iOS freezes timers in suspended apps), throttled, and best-effort: a
// failed check is just "no news", never an error.

const CHECK_MIN_INTERVAL_MS = 60_000;

let initialId: string | null = null;
let lastCheckAt = 0;
let bannerShown = false;

async function fetchVersionId(): Promise<string | null> {
  try {
    // no-store on the fetch: the SW already skips /api/*, but Safari's own
    // HTTP cache does not, and a cached answer would hide the deploy.
    const res = await fetch("/api/version", { cache: "no-store" });
    if (!res.ok) return null;
    const body = (await res.json()) as { id?: string };
    return typeof body.id === "string" ? body.id : null;
  } catch {
    return null; // offline / transient — no news
  }
}

function showBanner(): void {
  if (bannerShown) return;
  bannerShown = true;

  const bar = document.createElement("div");
  bar.setAttribute("role", "status");
  bar.style.cssText = [
    "position:fixed",
    "left:50%",
    "transform:translateX(-50%)",
    "bottom:calc(env(safe-area-inset-bottom, 0px) + 16px)",
    "z-index:9999",
    "display:flex",
    "align-items:center",
    "gap:12px",
    "background:#0e2a33",
    "color:#f0f7f9",
    "padding:10px 12px 10px 16px",
    "border-radius:999px",
    "box-shadow:0 8px 24px rgba(0,0,0,0.35)",
    "font:600 14px/1.2 'Hanken Grotesk Variable',system-ui,sans-serif",
    "max-width:calc(100vw - 32px)",
  ].join(";");

  const label = document.createElement("span");
  label.textContent = "A new version is ready";

  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = "Reload";
  btn.style.cssText = [
    "background:#2ec4b6",
    "color:#07211f",
    "border:0",
    "border-radius:999px",
    "padding:8px 16px",
    "font:700 14px/1 inherit",
    "font-family:inherit",
    "cursor:pointer",
  ].join(";");
  btn.addEventListener("click", () => {
    btn.disabled = true;
    btn.textContent = "Reloading…";
    // Nudge the SW to pick up its new script too; the navigation strategy is
    // network-first, so a plain reload serves the fresh shell while online.
    void navigator.serviceWorker
      ?.getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {})
      .finally(() => location.reload());
  });

  bar.appendChild(label);
  bar.appendChild(btn);
  document.body.appendChild(bar);
}

async function check(): Promise<void> {
  const now = Date.now();
  if (now - lastCheckAt < CHECK_MIN_INTERVAL_MS) return;
  lastCheckAt = now;

  const id = await fetchVersionId();
  if (!id || id === "dev") return;
  if (initialId === null) {
    initialId = id;
    return;
  }
  if (id !== initialId) showBanner();
}

export function startUpdateCheck(): void {
  // Baseline read at boot, then re-check whenever the user comes back.
  void check();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  window.addEventListener("focus", () => void check());
}
